import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    Typography,
    Box,
    Alert,
} from '@mui/material';
import { mergeIntegrationService } from 'services/mergeIntegration';
import { toast } from 'react-toastify';
import { getApiErrorMessage } from 'utilities/errorHandler';

interface ThresholdUpdateModalProps {
    open: boolean;
    onClose: () => void;
    currentThreshold: number;
    expertId: string;
    jobId: string;
    onUpdate: () => void;
}

const ThresholdUpdateModal: React.FC<ThresholdUpdateModalProps> = ({
    open,
    onClose,
    currentThreshold,
    expertId,
    jobId,
    onUpdate,
}) => {
    const [thresholdValue, setThresholdValue] = useState<string>('');
    const [applicationOption, setApplicationOption] = useState<string>('');
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setThresholdValue(currentThreshold.toString());
            setApplicationOption('');
            setShowConfirmation(false);
        }
    }, [open, currentThreshold]);

    const handleThresholdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setThresholdValue(e.target.value);
    };

    const handleOptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setApplicationOption(e.target.value);
    };

    const isValid = () => {
        const threshold = parseInt(thresholdValue, 10);
        return (
            !isNaN(threshold) &&
            threshold >= 0 &&
            threshold <= 100 &&
            applicationOption !== ''
        );
    };

    const handleSaveClick = () => {
        if (!isValid()) return;

        // Show confirmation for "existing and new" option
        if (applicationOption === 'both' && !showConfirmation) {
            setShowConfirmation(true);
            return;
        }

        handleSave();
    };

    const handleSave = async () => {
        const newThreshold = parseInt(thresholdValue, 10);

        setIsSaving(true);
        try {
            const response = await mergeIntegrationService.updateJobDetails({
                expertId,
                jobId,
                scoreThreshold: newThreshold,
                applyToExisting: applicationOption === 'both',
            });

            if (response?.apiStatus === 'SUCCESS') {
                // Show different success messages based on application option
                let successMessage = response?.apiMessage || 'Score threshold updated successfully';

                if (applicationOption === 'both') {
                    successMessage = 'Score threshold updated successfully. Existing candidates are being processed in the background and will be added to the talent pool shortly.';
                }

                toast.success(successMessage, {
                    autoClose: 5000, // Show for 5 seconds for the longer message
                });
                setShowConfirmation(false);
                onUpdate();
                onClose();
            } else {
                toast.error(getApiErrorMessage(response, 'Failed to update score threshold'));
                setShowConfirmation(false);
            }
        } catch (error: any) {
            console.error('Error updating score threshold:', error);
            toast.error(getApiErrorMessage(error, 'Error updating score threshold'));
            setShowConfirmation(false);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancelConfirmation = () => {
        setShowConfirmation(false);
    };

    const handleClose = () => {
        if (!isSaving) {
            onClose();
        }
    };

    return (
        <>
            <Dialog
                open={open && !showConfirmation}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Update Score Threshold</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Set a new threshold value and choose how it should be applied to candidates.
                        </Typography>

                        <TextField
                            label="Threshold Value (%)"
                            type="number"
                            value={thresholdValue}
                            onChange={handleThresholdChange}
                            fullWidth
                            inputProps={{ min: 0, max: 100 }}
                            sx={{
                                mb: 3,
                                '& input[type=number]': {
                                    MozAppearance: 'textfield',
                                },
                                '& input[type=number]::-webkit-outer-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                },
                                '& input[type=number]::-webkit-inner-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                },
                            }}
                            disabled={isSaving}
                        />

                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                            Apply threshold to:
                        </Typography>

                        <RadioGroup
                            value={applicationOption}
                            onChange={handleOptionChange}
                        >
                            <FormControlLabel
                                value="new"
                                control={<Radio />}
                                label={
                                    <Box>
                                        <Typography variant="body2" fontWeight={500}>
                                            New candidates only
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Apply the new threshold only to candidates added after this change
                                        </Typography>
                                    </Box>
                                }
                                disabled={isSaving}
                                sx={{ mb: 1.5, alignItems: 'flex-start' }}
                            />
                            <FormControlLabel
                                value="both"
                                control={<Radio />}
                                label={
                                    <Box>
                                        <Typography variant="body2" fontWeight={500}>
                                            Both existing and new candidates
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Apply the new threshold to all existing candidates and new candidates
                                        </Typography>
                                    </Box>
                                }
                                disabled={isSaving}
                                sx={{ alignItems: 'flex-start' }}
                            />
                        </RadioGroup>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleClose} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveClick}
                        variant="contained"
                        disabled={!isValid() || isSaving}
                    >
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog */}
            <Dialog
                open={showConfirmation}
                onClose={handleCancelConfirmation}
                maxWidth="sm"
            >
                <DialogTitle>Confirm Threshold Update</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This action will check existing candidates' scores and add eligible candidates to the talent pool.
                    </Alert>
                    <Typography variant="body2">
                        Are you sure you want to apply the new threshold ({thresholdValue}%) to both existing and new candidates?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleCancelConfirmation} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        color="warning"
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving...' : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ThresholdUpdateModal;
